import React, { useState, useEffect, useCallback } from 'react';
import {
  Col,
  Row,
  Container,
  Card,
  CardBody,
  TabPane,
  TabContent,
  Nav,
  NavItem,
  NavLink,
} from 'reactstrap';
import { useHistory } from 'react-router-dom';
import { useParams } from 'react-router';
import { toast } from 'react-toastify';
import moment from 'moment';
import Api from '../../util/api';
import t, { partial } from '../../util/translation/translation';
import Loading from '../../shared/components/custom/Loading';
import UserTierForm from './components/UserTierForm';
import UserTierPaymentForm from './components/UserToerPaymentsForm';

import UserTierList from './components/UserTierList';
import UnassingedUsersList from './components/UnassingedUserList';
import AssignTierPage from './components/AssignTierPage';
import UploadTierCSVPage from './components/UploadTierCSVPage';
import ImportTierMembersPage from './components/ImportTierMembersPage';
import NavTab from '../../shared/components/custom/NavTab';
import DeleteModal from '../../shared/components/custom/DeleteModal';
import useConfig from '../../util/useConfig';
import PaymentOptionsListPage from '../transactions/PaymentOptionsListPage';
import TransactionsListPage from '../transactions/TransactionsListPage';
// import config from '../../config';

const p = partial('UserTiers');
const s = partial('shared');

const UserTiersFormPage = () => {
  const config = useConfig();
  const [availableFeatures, setAvailableFeatures] = useState([]);
  const [deleteAffiliationId, setDeleteAffiliationId] = useState(null);
  const [currentTier, setCurrentTier] = useState(null);
  // The new import is shown first; the old CSV upload stays one click away
  const [useOldUpload, setUseOldUpload] = useState(false);
  const { tierID } = useParams();
  const [loading, setLoading] = useState(false);
  const [loadingAllTierUsers, setLoadingAllTierUsers] = useState(false);
  const [tierLoading, setTierLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('1');
  const [usersSubTab, setUsersSubTab] = useState('1');
  const [assignSubTab, setAssignSubTab] = useState('1');
  const [reverse, setReverse] = useState(false);
  const [tierUsers, setTierUsers] = useState([]);
  const [allTierUsers, setAllTierUsers] = useState([]);
  const [unassignedUsers, setUnassignedUsers] = useState([]);
  const [unassignedUsersTotalItems, setUnassignedUsersTotalItems] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [modalVis, setModalVis] = useState(false);

  const [pageData, setPageData] = useState({
    pageSize: 10,
    page: 1,
  });
  const editMode = tierID !== undefined;
  const history = useHistory();
  const getTierFeatures = async () => {
    setTierLoading(true);
    try {
      const promises = [Api.tiers.getTierFeatures(config)];
      if (tierID) {
        promises.push(Api.tiers.getTier(tierID, config));
      }
      const [featuresResp, tierResp] = await Promise.all(promises);
      setAvailableFeatures(featuresResp.data);
      setCurrentTier(tierResp?.data);
    } catch (err) {
      toast.error(err);
    } finally {
      setTierLoading(false);
    }
  };
  const getTierUsers = async () => {
    setLoading(true);
    try {
      const { pageSize, page } = pageData;
      const {
        data: { tierData, total },
      } = await Api.tiers.getTierUsers(tierID, page, pageSize, reverse);
      const cloudIds = tierData.map((item) => item.cloudId);
      const { data: userData } = await Api.users.getUsersByCloudIds(cloudIds);
      const combinedData = userData
        .map((usr) => {
          const foundAffiliation = tierData.find(
            (item) => item.cloudId === usr.userCloudId
          );
          return {
            ...usr,
            tier: {
              dueDate: foundAffiliation.dueDate,
              name: foundAffiliation.tierName,
              affiliationId: foundAffiliation.affiliationId,
            },
          };
        })
        .sort((a, b) => {
          const first = reverse ? b : a;
          const second = reverse ? a : b;
          return moment(first.tier.dueDate).diff(moment(second.tier.dueDate));
        });
      setTierUsers(combinedData);
      setTotalItems(total);
    } catch (err) {
      toast.error(err);
    } finally {
      setLoading(false);
    }
  };
  const getAllTierUsers = async () => {
    setLoadingAllTierUsers(true);
    try {
      const {
        data: { tierData },
      } = await Api.tiers.getTierUsers(tierID, 1, 10, reverse, true);
      const cloudIds = tierData.map((item) => item.cloudId);
      const { data: userData } = await Api.users.getUsersByCloudIds(cloudIds);
      const combinedData = userData
        .map((usr) => {
          const foundAffiliation = tierData.find(
            (item) => item.cloudId === usr.userCloudId
          );
          return {
            ...usr,
            tier: {
              dueDate: foundAffiliation.dueDate,
              name: foundAffiliation.tierName,
              affiliationId: foundAffiliation.affiliationId,
            },
          };
        })
        .sort((a, b) => {
          const first = reverse ? b : a;
          const second = reverse ? a : b;
          return moment(first.tier.dueDate).diff(moment(second.tier.dueDate));
        });
      setAllTierUsers(combinedData);
    } catch (err) {
      toast.error(err);
    } finally {
      setLoadingAllTierUsers(false);
    }
  };

  const getUnassignedUsers = useCallback(async () => {
    setLoading(true);
    try {
      const { pageSize, page } = pageData;
      const {
        data: { data, pagination },
      } = await Api.tiers.getUnassignedUsers(tierID, page, pageSize);
      setUnassignedUsers(data);
      setUnassignedUsersTotalItems(pagination?.total);
    } catch (err) {
      toast.error(err);
    } finally {
      setLoading(false);
    }
  }, [pageData, tierID]);
  const onChangePage = (pager) => {
    const { pageSize, currentPage } = pager;
    if (pageData.page !== currentPage || pageData.pageSize !== pageSize) {
      setPageData({
        pageSize,
        page: currentPage,
      });
    }
  };
  const handleModal = (state, deleteId) => {
    setModalVis(state);
    setDeleteAffiliationId(deleteId);
  };
  const deleteAffiliation = async () => {
    setLoading(true);
    try {
      await Api.tiers.deleteUserAffiliation(deleteAffiliationId, config);
      handleModal(false, null);
      getTierUsers();
    } catch (err) {
      toast.error(err);
      setLoading(false);
    }
  };
  useEffect(() => {
    getTierFeatures();
  }, []);
  useEffect(() => {
    if (editMode) {
      getTierUsers();
      getAllTierUsers();
      getUnassignedUsers();
    }
  }, [pageData, reverse, getUnassignedUsers]);
  const onSubmit = async (values) => {
    const {
      name,
      description,
      pickedFeatures,
      previousPrice,
      image,
      cardBackground,
      price1,
      price12,
    } = values;

    // Dropdown options are edited as one comma-separated string for
    // simplicity; split them into an array only when building the payload.
    const customFields = (values.customFields || []).map((field) => {
      if (field.type !== 'dropdown') return field;
      const { optionsText, ...rest } = field;
      // Without the text (form filled by the details tab) keep the options as
      // they are
      if (optionsText === undefined) return rest;
      return {
        ...rest,
        options: optionsText
          .split(',')
          .map((option) => option.trim())
          .filter(Boolean),
      };
    });

    // const description = draftToHtml(convertToRaw(editorState.getCurrentContent()));
    const payload = {
      description,
      price1,
      // price3,
      // price6,
      price12,
      featureIds: pickedFeatures?.map((item) => item.id) || [],
      name,
      previousPrice: previousPrice || undefined,
      currency: config.currencyCaps,
      forSale: values.forSale || false,
      visible: values.visible || false,
      sharable: values.sharable || false,
      sharedViaApi: !!values.sharedViaApi,
      visiblePrice: values.visiblePrice,
      salesDescription: values.salesDescription,
      // Empty sends null so a cleared text is removed
      salesTitle: values.salesTitle?.trim() || null,
      badgeText: values.badgeText?.trim() || null,
      salesHighlighted: !!values.salesHighlighted,
      customFields,
    };
    setLoading(true);
    try {
      if (editMode) {
        payload.image =
          (await image?.promise?.('tier', 'originalUrl', tierID)) ||
          image?.preview.includes('blob')
            ? undefined
            : image?.preview || null;
        payload.image1 =
          (await cardBackground?.promise?.(
            'tier-background-image',
            'cardUrl',
            tierID
          )) || cardBackground?.preview.includes('blob')
            ? undefined
            : cardBackground?.preview || null;
        await Api.tiers.editTier(tierID, payload, config);
      } else {
        const { data: resTier } = await Api.tiers.createTier(payload, config);
        await image?.promise?.('tier', 'originalUrl', resTier.id);
        await cardBackground?.promise?.(
          'tier-background-image',
          'cardUrl',
          resTier.id
        );
      }
      setLoading(false);
      toast.success(p('tierSaved'));
      history.replace('/user_tiers');
    } catch (err) {
      setLoading(false);
      toast.error(s('somethingWentWrong'));
    }
  };
  const toggle = (tab) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
    }
  };
  const toggleUsersSubTab = (tab) => {
    if (usersSubTab !== tab) {
      setUsersSubTab(tab);
    }
  };
  const toggleAssignSubTab = (tab) => {
    if (assignSubTab !== tab) {
      setAssignSubTab(tab);
    }
  };
  return (
    <Container>
      <Loading loading={loading || tierLoading || loadingAllTierUsers} />
      <DeleteModal
        type={p('tier')}
        visible={modalVis}
        handleModal={handleModal}
        modalAction={() => deleteAffiliation()}
      />
      <Row>
        <Col md={12}>
          <Card>
            <CardBody className="p-0">
              <NavTab
                tabs={[
                  s('details'),
                  { disabled: !editMode, tab: p('websales') },
                  { disabled: !editMode, tab: s('transactions') },
                  { disabled: !editMode, tab: s('users') },
                  { disabled: !editMode, tab: s('assignUser') },
                ]}
                activeTab={activeTab}
                setTab={toggle}
                size="sm"
              />
              <div className="tabs tabs--justify tabs--bordered-top overflow-unset mx-3">
                <div className="tabs__wrap">
                  <TabContent activeTab={activeTab}>
                    <TabPane tabId="1">
                      <UserTierForm
                        tier={currentTier}
                        onSubmit={onSubmit}
                        availableFeatures={availableFeatures}
                      />
                    </TabPane>
                    {editMode && (
                      <>
                        <TabPane tabId="2">
                          <PaymentOptionsListPage
                            entityId={tierID}
                            type="tier"
                          />
                          <hr />
                          <UserTierPaymentForm
                            tier={currentTier}
                            onSubmit={onSubmit}
                            availableFeatures={availableFeatures}
                          />
                        </TabPane>
                        <TabPane tabId="3">
                          <TransactionsListPage entityId={tierID} />
                        </TabPane>
                        <TabPane tabId="4">
                          <TabContent activeTab={usersSubTab}>
                            <TabPane tabId="1">
                              <UserTierList
                                title={
                                  <>
                                    <div className="form-card__switch d-none d-md-inline-flex">
                                      <button
                                        type="button"
                                        className={`form-card__switch-btn${
                                          usersSubTab === '1' ? ' active' : ''
                                        }`}
                                        onClick={() => toggleUsersSubTab('1')}
                                      >
                                        {s('users')}
                                      </button>
                                      <button
                                        type="button"
                                        className={`form-card__switch-btn${
                                          usersSubTab === '2' ? ' active' : ''
                                        }`}
                                        onClick={() => toggleUsersSubTab('2')}
                                      >
                                        {p('unassignedUsers')} (
                                        {unassignedUsersTotalItems})
                                      </button>
                                    </div>
                                    <span className="d-md-none">
                                      {s('users')}
                                    </span>
                                  </>
                                }
                                handleModal={handleModal}
                                tierUsers={tierUsers}
                                allTierUsers={allTierUsers}
                                totalItems={totalItems}
                                setReverse={setReverse}
                                reverse={reverse}
                                onChangePage={onChangePage}
                              />
                            </TabPane>
                            <TabPane tabId="2">
                              <UnassingedUsersList
                                title={
                                  <>
                                    <div className="form-card__switch d-none d-md-inline-flex">
                                      <button
                                        type="button"
                                        className={`form-card__switch-btn${
                                          usersSubTab === '1' ? ' active' : ''
                                        }`}
                                        onClick={() => toggleUsersSubTab('1')}
                                      >
                                        {s('users')}
                                      </button>
                                      <button
                                        type="button"
                                        className={`form-card__switch-btn${
                                          usersSubTab === '2' ? ' active' : ''
                                        }`}
                                        onClick={() => toggleUsersSubTab('2')}
                                      >
                                        {p('unassignedUsers')} (
                                        {unassignedUsersTotalItems})
                                      </button>
                                    </div>
                                    <span className="d-md-none">
                                      {p('unassignedUsers')} (
                                      {unassignedUsersTotalItems})
                                    </span>
                                  </>
                                }
                                tierUsers={unassignedUsers}
                                totalItems={unassignedUsersTotalItems}
                                onChangePage={onChangePage}
                              />
                            </TabPane>
                          </TabContent>
                        </TabPane>
                        <TabPane tabId="5">
                          <Nav pills className="mb-2">
                            <NavItem>
                              <NavLink
                                active={assignSubTab === '1'}
                                onClick={() => toggleAssignSubTab('1')}
                              >
                                {t('TierImport.title')}
                              </NavLink>
                            </NavItem>
                            <NavItem>
                              <NavLink
                                active={assignSubTab === '2'}
                                onClick={() => toggleAssignSubTab('2')}
                              >
                                {p('searchUser')}
                              </NavLink>
                            </NavItem>
                          </Nav>
                          <TabContent activeTab={assignSubTab}>
                            <TabPane tabId="1">
                              {useOldUpload ? (
                                <UploadTierCSVPage
                                  tierID={tierID}
                                  currentTier={currentTier}
                                  getTierUsers={getTierUsers}
                                  changeTab={toggle}
                                />
                              ) : (
                                <ImportTierMembersPage
                                  tierID={tierID}
                                  tier={currentTier}
                                  getTierUsers={getTierUsers}
                                  onUseOldUpload={() => setUseOldUpload(true)}
                                />
                              )}
                            </TabPane>
                            <TabPane tabId="2">
                              <AssignTierPage
                                tierID={tierID}
                                getTierUsers={getTierUsers}
                              />
                            </TabPane>
                          </TabContent>
                        </TabPane>
                      </>
                    )}
                  </TabContent>
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default UserTiersFormPage;
